import { CalendarClock } from "lucide-react";
import { useLang } from "../lib/LangContext";
import { t as tRaw } from "../lib/i18n";

// Temporary launch-notice pill rendered next to every WhatsApp Daily
// Digest CTA. Displayed while the digest is still awaiting Meta / Twilio
// business verification (expected early October 2026) so visitors who
// subscribe understand the digest isn't sending yet and are opting into
// the waitlist rather than an active feed.
//
// Removal mechanism (2026-02, per Calvin): the whole component
// short-circuits when `REACT_APP_DIGEST_LIVE=true` is set on the
// frontend `.env`. Flipping that single flag on launch day hides the
// notice from every placement sitewide with zero code changes.
//
// Language sourcing: most placements sit inside a per-page
// LangContext.Provider (HeroCTAStrip, Sidebar, ArticleCTALine, Footer,
// WhatsAppModal via SubscribeModalHost). A handful — MobileStickyBar,
// FloatingSubscribeCTA — live outside the provider tree and read
// `localStorage.ir_lang` themselves. Those can pass their resolved
// `lang` as a prop; the component falls back to the LangContext value
// when no explicit prop is provided.
//
// Variants: `full` for roomy placements (hero strip, sidebar card,
// modal body, footer column) and `short` for space-constrained spots
// (in-article line, mobile sticky bar tooltip, floating CTA tooltip).
export const DigestLaunchNotice = ({
  variant = "full",
  tone = "light",
  lang: langProp,
  className = "",
  testid = "digest-launch-notice",
}) => {
  // Hooks first — React's rules-of-hooks forbids conditional hook
  // invocation, so read the context even when we intend to bail on the
  // feature-flag check below.
  const ctxLang = useLang();

  // Feature-flag short-circuit — flip REACT_APP_DIGEST_LIVE=true on
  // launch day and the notice disappears everywhere it was mounted.
  if (process.env.REACT_APP_DIGEST_LIVE === "true") return null;

  const lang = langProp || ctxLang;
  const key = variant === "short"
    ? "digest_launch_notice_short"
    : "digest_launch_notice";
  const text = tRaw(lang, key);

  // Two tone treatments — `light` for white/neutral cards (footer,
  // in-article), `dark` for the dark-teal WhatsApp CTA cards
  // (HeroCTAStrip, Sidebar, MobileStickyBar). Both keep an amber
  // family palette (the honest "heads up" colour) but pick contrast
  // against their respective backgrounds.
  const toneClasses = tone === "dark"
    ? "bg-amber-400/15 border-amber-300/40 text-amber-100"
    : "bg-amber-50 border-amber-300 text-amber-900";

  return (
    <div
      role="status"
      data-testid={testid}
      className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-[12px] leading-snug ${toneClasses} ${className}`}
    >
      <CalendarClock size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
      <span>{text}</span>
    </div>
  );
};
